<template>
    <v-dialog
        v-model="isActive"
        width="700"
        persistent
        @click:outside.prevent="closeAndClear"
    >
        <div
            v-if="isActive"
            class="shipment-payment-add-modal"
            :class="{ ['is-loading']: isLoading }"
        >
            <div class="shipment-payment-add-modal__header">
                <h3 class="shipment-payment-add-modal__title">
                    {{ $t('pages.payments.addPayment') }}
                </h3>
                <v-btn icon color="#000" large @click="closeAndClear">
                    <v-icon>mdi-close-circle-outline</v-icon>
                </v-btn>
            </div>

            <div class="shipment-payment-add-modal__body">
                <div>
                    <v-select
                        v-model="selectedPaymentType"
                        :items="paymentTypes"
                        :item-text="getTranslationForPaymentTypes"
                        item-value="payment_type"
                        :label="$t('pages.payments.selectPaymentType')"
                        outlined
                        dense
                        class="payment-select"
                    ></v-select>

                    <component
                        :is="paymentFormComponent"
                        v-bind="computedPaymentData"
                        @update-payment="updatePaymentData"
                    ></component>
                </div>

                <div class="shipment-payment-add-modal__buttons">
                    <v-btn
                        class="shipment-payment-add-modal__save"
                        @click="onSave"
                    >
                        {{ $t('pages.payments.save') }}
                    </v-btn>
                    <v-btn
                        v-if="selectedPaymentType === 1"
                        class="shipment-payment-add-modal__save"
                        @click="onContinue"
                    >
                        {{ $t('pages.payments.addAnotherCheck') }}
                    </v-btn>
                </div>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import languageMixin from '@/mixins/language.mixin'
import { paymentsV2GetterTypes } from '@/store/modules/payments_v2/types'
import PaymentCashForm from '@/components/payments/payment-cash-form.vue'
import ShipmentPaymentCardForm from '@/components/shipments/shipment-payment-card-form.vue'
import PaymentCheckForm from '@/components/payments/payment-check-form.vue'
import PaymentTransferForm from '@/components/payments/payment-transfer-form.vue'
import shortid from 'shortid'

export default {
    name: 'ShipmentPaymentAddModal',
    components: {
        PaymentCashForm,
        PaymentCheckForm,
        ShipmentPaymentCardForm,
        PaymentTransferForm,
    },
    mixins: [languageMixin],
    props: {
        document: {
            type: Object,
            required: true,
        },
    },
    emits: ['payment-added', 'close'],
    data() {
        return {
            isLoading: false,
            selectedPaymentType: null,
            paymentData: {},
            isActive: true,
        }
    },
    computed: {
        paymentTypes() {
            if (!this.isActive) return []
            const allTypes =
                this.$store.getters[
                    'paymentsV2/' + paymentsV2GetterTypes.GET_PAYMENT_TYPES
                ] || []
            return allTypes.filter((type) => type.payment_type !== 100)
        },
        paymentFormComponent() {
            return this.getPaymentFormComponent(this.selectedPaymentType)
        },
        computedPaymentData() {
            return this.paymentData
        },
    },
    watch: {
        document: {
            handler() {
                this.isActive = true
            },
            immediate: true,
        },
    },
    methods: {
        getPaymentFormComponent(type_id) {
            if (type_id === 1) {
                return 'PaymentCheckForm'
            }
            if (type_id === 2) {
                return 'PaymentCashForm'
            }
            if (type_id === 9) {
                return 'PaymentTransferForm'
            }
            if (this.isCard(type_id)) {
                return 'ShipmentPaymentCardForm'
            }
        },
        isCard(payment_type) {
            return [4, 5, 6, 7, 8, 10, 11].includes(payment_type)
        },
        updatePaymentData(newData) {
            this.paymentData = { ...this.paymentData, ...newData }
        },
        processPaymentData() {
            const processedData = {
                ...this.paymentData,
                payment_type: this.selectedPaymentType,
                local_id: shortid(),
            }
            
            if (this.isCard(this.selectedPaymentType)) {
                processedData.quantity = 1
                processedData.amount = parseFloat(processedData.amount || 0)
                processedData.due_date = this.calculateNewDate(1)
            }
            
            return processedData
        },
        validatePaymentData(paymentType, data) {
            if (!data.amount || parseFloat(data.amount) <= 0) return false
            if (!data.due_date) return false

            if (paymentType === 1) {
                return !!(
                    data.check_number &&
                    data.bank_code &&
                    data.branch_number &&
                    data.bank_account_number
                )
            }

            if (paymentType === 2 || paymentType === 9) {
                return true
            }

            if (this.isCard(paymentType)) {
                return data.bank_account_number;

            }

            return false
        },
        onContinue: async function () {
            if (
                !this.validatePaymentData(
                    this.selectedPaymentType,
                    this.paymentData
                )
            ) {
                return
            }

            const processedData = this.processPaymentData()
            this.$emit('payment-added', processedData)

            // Don't close modal, just update date and check number for next payment
            const newDate = new Date(this.paymentData.due_date)
            newDate.setMonth(newDate.getMonth() + 1)

            this.paymentData = {
                ...this.paymentData,
                due_date: newDate.toISOString().split('T')[0],
                check_number: (
                    parseInt(this.paymentData.check_number || 0) + 1
                ).toString(),
            }
        },
        calculateNewDate(i) {
            const currentDate = new Date(this.paymentData.due_date)
            const currentDay = currentDate.getDate()
            const currentMonth = currentDate.getMonth()
            const currentYear = currentDate.getFullYear()

            const totalMonths = currentMonth + i
            const nextYear = currentYear + Math.floor(totalMonths / 12)
            const nextMonth = totalMonths % 12

            const dueDay = currentDay <= 15 ? 3 : 9

            return new Date(nextYear, nextMonth, dueDay)
                .toISOString()
                .slice(0, 10)
        },
        onCardPayment: async function () {
            const processedData = this.processPaymentData()
            this.$emit('payment-added', processedData)
            await this.closeAndClear()
        },
        onSave: async function () {
            if (
                !this.validatePaymentData(
                    this.selectedPaymentType,
                    this.paymentData
                )
            ) {
                return
            }

            if (this.isCard(this.selectedPaymentType)) {
                await this.onCardPayment()
                return
            }

            const processedData = this.processPaymentData()
            this.$emit('payment-added', processedData)

            await this.closeAndClear()
        },
        clearPaymentData() {
            this.paymentData = {}
            this.selectedPaymentType = null
        },
        closeAndClear() {
            this.clearPaymentData()
            this.isActive = false
            this.$emit('close')
        },
        getTranslationForPaymentTypes(item) {
            const translatedKey = `pages.payments.${item.payment_name}`
            const translatedText = this.$t(translatedKey)

            if (translatedText.includes('Translation not found for key')) {
                return item.payment_name
            }

            return translatedText
        },
    },
}
</script>

<style lang="scss" scoped>
.shipment-payment-add-modal {
    background: $color-primary-light;
    position: relative;

    &.is-loading {
        overflow: hidden;
        max-height: 90vh;
    }

    &__title {
        font-size: 22px;
        font-weight: 400;
        color: #000;
    }

    &__header {
        padding: 22px 16px;
        border-bottom: 1px solid #eceeef;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    &__body {
        width: 100%;
        height: 100%;
        padding: 16px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    &__save {
        background: #37bc9b !important;
        color: white !important;
        padding: 16px !important;
        font-size: 16px !important;
        flex: 1 !important;

        &:nth-child(2) {
            background: #967adc !important;
        }
    }

    &__buttons {
        display: flex;
        justify-content: center;
        gap: 16px;
        width: 100%;
        align-items: center;
    }

    .payment-fields {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 0;
        width: 100%;
    }

    .payment-field {
        max-width: 100% !important;
        min-width: 100%;
    }
}
</style>
